import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer } from "@outpave/ds";
import { Box, Tab, Tabs } from "@mui/material";
import MapYourCategoriesDrawer from "./MapYourCategoriesDrawer";
import AllocationSettingsDrawer from "../allocations/AllocationSettings";
import SyncSettingsDrawer from "./SyncSettings";

interface IntegrationSettingsDrawerProps {
  integrationType: string;
  open: boolean;
  handleCloseDrawer: () => void;
}

type MappingTab = "category" | "allocation" | "settings";

const IntegrationSettingsDrawer: React.FC<IntegrationSettingsDrawerProps> = (
  props,
) => {
  const { integrationType, open, handleCloseDrawer } = props;
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<MappingTab>("category");
  // The panels own their save logic, so they render their own buttons, but the
  // package takes one footer on the parent. A portal host keeps the buttons in
  // the panel's tree, where their handlers and disabled state stay current,
  // and puts their DOM in the drawer's footer.
  const [footerHost, setFooterHost] = useState<HTMLDivElement | null>(null);

  const onCloseDrawer = () => {
    handleCloseDrawer();
  };

  const handleChangeTab = (
    event: React.SyntheticEvent,
    newValue: MappingTab,
  ) => {
    setActiveTab(newValue);
  };

  return (
    // `disablePadding` because the tab strip runs to the panel edges; each tab
    // body brings its own padding, as does the footer row inside it.
    <Drawer
      open={open}
      onClose={onCloseDrawer}
      title={t("EditIntegration")}
      width={560}
      disablePadding
      footer={<Box ref={setFooterHost} sx={{ display: "contents" }} />}
    >
      {/* Sticky so the strip stays put while a long category list scrolls in
          the drawer's own ScrollArea. The background is not decoration: a
          transparent sticky strip shows the rows passing under it. */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          flexShrink: 0,
          backgroundColor: "var(--color-bg-surface)",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          aria-label={t("Mapping sections")}
        >
          <Tab
            value="category"
            id="mapping-tab-category"
            aria-controls="mapping-panel-category"
            label={t("Category")}
          />
          <Tab
            value="allocation"
            id="mapping-tab-allocation"
            aria-controls="mapping-panel-allocation"
            label={t("Allocation")}
          />
          <Tab
            value="settings"
            id="mapping-tab-settings"
            aria-controls="mapping-panel-settings"
            label={t("Settings")}
          />
        </Tabs>
      </Box>

      {activeTab === "category" && (
        <Box
          role="tabpanel"
          id="mapping-panel-category"
          aria-labelledby="mapping-tab-category"
        >
          <MapYourCategoriesDrawer
            open={open}
            integrationType={integrationType}
            handleCloseDrawer={handleCloseDrawer}
            footerHost={footerHost}
          />
        </Box>
      )}
      {activeTab === "allocation" && (
        <Box
          role="tabpanel"
          id="mapping-panel-allocation"
          aria-labelledby="mapping-tab-allocation"
        >
          <AllocationSettingsDrawer
            integrationType={integrationType}
            open={open}
            handleCloseDrawer={handleCloseDrawer}
            footerHost={footerHost}
          />
        </Box>
      )}
      {activeTab === "settings" && (
        <Box
          role="tabpanel"
          id="mapping-panel-settings"
          aria-labelledby="mapping-tab-settings"
        >
          <SyncSettingsDrawer
            open={open}
            handleCloseDrawer={handleCloseDrawer}
            footerHost={footerHost}
          />
        </Box>
      )}
    </Drawer>
  );
};

export default IntegrationSettingsDrawer;
